import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useMobileScreen from "./useMobileScreen";
import useSearchParams from "./useSearchParams";
import { layerMobileProps, layerProps } from "../constants/mapConstants";
import { useTypedSelector } from "./useTypedSelector";

const useMapObjects = () => {
  const { viewport } = useTypedSelector((state) => state.mapInterface);
  const [currObjs, setCurrObjs] = useState<GeoJSON.Feature[] | []>([]);
  const [pinnedCurrObjs, setPinnedCurrObjs] = useState<GeoJSON.Feature[] | []>(
    []
  );
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const hidePopupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const isMobile = useMobileScreen();
  const { setSearchParam } = useSearchParams();

  const clearHidePopupTimeout = useCallback(() => {
    if (hidePopupTimeoutRef.current) {
      clearTimeout(hidePopupTimeoutRef.current);
      hidePopupTimeoutRef.current = null;
    }
  }, []);

  const hidePopupWithDelay = useCallback(() => {
    clearHidePopupTimeout();
    hidePopupTimeoutRef.current = setTimeout(() => {
      setCurrObjs([]);
      hidePopupTimeoutRef.current = null;
    }, 0);
  }, [clearHidePopupTimeout]);

  const markerHoverHandler = useCallback(
    (features?: GeoJSON.Feature[] | []) => {
      if (!isMobile) {
        if (features && features.length) {
          clearHidePopupTimeout();
          setCurrObjs(features);
        } else if (!isPopupHovered) {
          hidePopupWithDelay();
        }
      }
    },
    [clearHidePopupTimeout, hidePopupWithDelay, isMobile, isPopupHovered]
  );

  const handlePopupMouseEnter = useCallback(() => {
    setIsPopupHovered(true);
    clearHidePopupTimeout();
  }, [clearHidePopupTimeout]);

  const handlePopupMouseLeave = useCallback(() => {
    setIsPopupHovered(false);
    hidePopupWithDelay();
  }, [hidePopupWithDelay]);

  useEffect(() => {
    return () => {
      clearHidePopupTimeout();
    };
  }, [clearHidePopupTimeout]);

  const markerClickHandler = (
    features: GeoJSON.Feature[] | [],
    hashIdKey = "hash_id"
  ) => {
    if (!isMobile) {
      if (currObjs.length === 1) {
        setSearchParam("hash_id", currObjs[0].properties[hashIdKey]);
      }
    } else {
      if (features?.length) {
        if (features.length === 1) {
          setSearchParam("hash_id", features[0].properties[hashIdKey]);
        } else {
          setPinnedCurrObjs(features);
        }
      } else {
        setPinnedCurrObjs([]);
      }
    }
  };

  const selectedLayerProps = useMemo(() => {
    if (isMobile) {
      return layerMobileProps;
    } else {
      if (viewport.zoom > 12) {
        return layerMobileProps;
      } else {
        return layerProps;
      }
    }
  }, [isMobile, viewport.zoom]);

  return {
    currObjs,
    setCurrObjs,
    pinnedCurrObjs,
    setPinnedCurrObjs,
    markerClickHandler,
    markerHoverHandler,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    selectedLayerProps,
  };
};

export default useMapObjects;
